import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getPageBySlug } from '../../utils/wordpressApi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ElectricalSlider from '../../components/ElectricalSlider/ElectricalSlider';
import './Home.css';

// Import defaultowych zdjęć
import defaultImage1 from '../../assets/images/home/1.jpg';

const Home = () => {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Defaultowe treści
  const defaultContent = {
    title: 'Rafloor Elektryka i Teletechnika',
    firstParagraph: 'Firma Elektryka i Teletechnika specjalizuje się w kompleksowych usługach elektrycznych i teletechnicznych dla klientów indywidualnych oraz biznesowych. Oferujemy instalacje elektryczne, systemy alarmowe, monitoring CCTV oraz okablowanie strukturalne, zapewniając bezpieczeństwo i nowoczesne rozwiązania. Działamy rzetelnie, terminowo i z pełnym zaangażowaniem.',
    remainingContent: `
      <p>Nasz zespół tworzą wykwalifikowani fachowcy z wieloletnim doświadczeniem, którzy dbają o najwyższą jakość wykonania i indywidualne podejście do każdego zlecenia. Współpracujemy zarówno z osobami prywatnymi, jak i firmami, oferując doradztwo techniczne i realizację inwestycji od projektu po serwis. Dzięki nowoczesnemu zapleczu technologicznemu jesteśmy w stanie sprostać nawet najbardziej wymagającym realizacjom.</p>
      <figure class="wp-block-image size-full">
        <img src="${defaultImage1}" alt="Rafloor Elektryka i Teletechnika" class="wp-image-22"/>
      </figure>
      <p>W Rafloor stawiamy na rozwój i innowacje – regularnie poszerzamy naszą ofertę oraz wdrażamy najnowsze rozwiązania z zakresu automatyki budynkowej i energooszczędnych instalacji. Naszym priorytetem jest zadowolenie klienta, dlatego każdy projekt realizujemy z najwyższą starannością, zgodnie z obowiązującymi normami i przepisami. Zapraszamy do kontaktu – chętnie doradzimy i przygotujemy ofertę dopasowaną do Twoich potrzeb.</p>
    `
  };

  useEffect(() => {
    const fetchHomePage = async () => {
      try {
        setLoading(true);
        // Próbujemy pobrać dane z WordPress
        const homePage = await getPageBySlug('strona-glowna');
        setPage(homePage);
      } catch (err) {
        // Jeśli nie udało się pobrać z WP, używamy defaultowych treści
        console.warn('Could not fetch WordPress content, using default content:', err);
        setPage(null); // Pozostawiamy null żeby użyć defaultowych treści
        setError(null); // Nie pokazujemy błędu, bo mamy fallback
      } finally {
        setLoading(false);
      }
    };

    fetchHomePage();
  }, []);

  // Helper function to extract first paragraph and remaining content
  const extractContentParts = (htmlContent) => {
    if (!htmlContent) return { firstParagraph: '', remainingContent: '' };
    
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    
    const firstP = tempDiv.querySelector('p');
    const firstParagraph = firstP ? firstP.textContent : '';
    
    if (firstP) {
      firstP.remove();
    }
    
    return {
      firstParagraph,
      remainingContent: tempDiv.innerHTML
    };
  };

  if (loading) {
    return <LoadingSpinner text="Ładowanie strony głównej..." />;
  }

  // Używamy treści z WordPress lub defaultowych
  const contentParts = page ? extractContentParts(page.content?.rendered) : { firstParagraph: '', remainingContent: '' };
  
  const title = page?.title?.rendered || defaultContent.title;
  const firstParagraph = contentParts.firstParagraph || defaultContent.firstParagraph;
  const remainingContent = contentParts.remainingContent || defaultContent.remainingContent;

  return (
    <div className="home-page">
      {/* Hero Section z tytułem, sliderem, tekstem i przyciskami */}
      <section className="hero-section">
        <div className="hero-content">
          {/* Najpierw slider pełnoekranowy */}
          <div className="hero-slider animate-fade-in-up">
            <ElectricalSlider />
          </div>

          {/* Potem tytuł na fioletowym tle */}
          <div className="hero-title animate-fade-in-up animation-delay-100">
            <h1>{title}</h1>
          </div>

          {/* Tekst pod tytułem */}
          <div className="hero-text animate-fade-in-up animation-delay-200">
            <p className="hero-subtitle">
              {firstParagraph}
            </p>
          </div>

          {/* Przyciski */}
          <div className="hero-buttons animate-fade-in-up animation-delay-300">
            <Link to="/kontakt" className="btn btn-primary">
              Skontaktuj się z nami
            </Link>
            <Link to="/nasze-realizacje" className="btn btn-secondary">
              Zobacz nasze realizacje
            </Link>
          </div>
        </div>
      </section>

      {/* Main Content from WordPress or Default */}
      {remainingContent && (
        <section className="content-section">
          <div className="container">
            <div className="wordpress-content">
              <div 
                dangerouslySetInnerHTML={{ __html: remainingContent }}
                className="content-wrapper"
              />
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      <section className="features-section section">
        <div className="container">
          <h2 className="section-title animate-fade-in-up">
            Dlaczego Rafloor?
          </h2>
          <div className="features-grid">
            <div className="feature-card animate-fade-in-up animation-delay-100">
              <div className="feature-icon">⚡</div>
              <h3>Doświadczenie</h3>
              <p>Ponad 15 lat doświadczenia w branży elektrycznej i teletechnicznej</p>
            </div>
            <div className="feature-card animate-fade-in-up animation-delay-200">
              <div className="feature-icon">🔧</div>
              <h3>Profesjonalizm</h3>
              <p>Wykwalifikowany zespół z odpowiednimi certyfikatami i uprawnieniami</p>
            </div>
            <div className="feature-card animate-fade-in-up animation-delay-300">
              <div className="feature-icon">🛡️</div>
              <h3>Gwarancja</h3>
              <p>Pełne ubezpieczenie OC i długoterminowa gwarancja na wszystkie usługi</p>
            </div>
            <div className="feature-card animate-fade-in-up animation-delay-400">
              <div className="feature-icon">📞</div>
              <h3>Dostępność 24/7</h3>
              <p>Szybka reakcja na awarie i problemy - jesteśmy dostępni całą dobę</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content animate-fade-in-up">
            <h2>Gotowy na współpracę?</h2>
            <p>Skontaktuj się z nami już dziś i otrzymaj bezpłatną wycenę</p>
            <Link to="/kontakt" className="btn btn-primary btn-large">
              Otrzymaj wycenę
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;